import { FieldNumberValue } from '@/interfaces/general/FieldNumberValue';
import moment from 'moment';
import store from '@/store';
import UserGroupRepository from '@/repositories/UserGroupRepository';

/**
 * Maps country codes to their corresponding locale for number formatting.
 * This ensures numbers are formatted according to the user's country,
 * not their language preference.
 *
 * Note: Slovenia uses space as thousands separator (1 000,00), so we use 'de-AT'
 * which provides this format, rather than 'sl-SI' which uses dots (1.000,00).
 */
const COUNTRY_TO_LOCALE: Record<string, string> = {
    SI: 'de-AT', // Slovenia - uses space as thousands separator (1 000,00)
    IT: 'it-IT', // Italy
    GB: 'en-GB', // United Kingdom
    DE: 'de-DE', // Germany
    AT: 'de-AT', // Austria
    HR: 'hr-HR', // Croatia
    FR: 'fr-FR', // France
    ES: 'es-ES', // Spain
    NL: 'nl-NL', // Netherlands
    BE: 'nl-BE', // Belgium
    CH: 'de-CH', // Switzerland
    PL: 'pl-PL', // Poland
    CZ: 'cs-CZ', // Czech Republic
    SK: 'sk-SK', // Slovakia
    HU: 'hu-HU', // Hungary
    RO: 'ro-RO', // Romania
    RS: 'sr-RS', // Serbia
    BA: 'bs-BA', // Bosnia and Herzegovina
    ME: 'sr-ME', // Montenegro
    MK: 'mk-MK', // Macedonia
    US: 'en-US', // United States
};

// Default locale if country is not found (uses space as thousands separator)
const DEFAULT_LOCALE = 'de-AT';

/**
 * Gets the locale for number formatting based on the current user's country.
 * Uses the country from the user's UserGroup, NOT the language preference.
 */
export function getNumberFormattingLocale(): string {
    try {
        const currentUserGroupId = store.getters['jwtData/currentUserGroup'];
        const userGroup = UserGroupRepository.getById(currentUserGroupId);
        const countryCode = userGroup?.clientCountryId?.code;

        if (countryCode) {
            return COUNTRY_TO_LOCALE[countryCode.toUpperCase()] || DEFAULT_LOCALE;
        }
    } catch (e) {
        // Fallback to default if store is not available
    }

    return DEFAULT_LOCALE;
}

/**
 * Formats a number according to the user's country locale.
 * This is the main function to use across the app for consistent number formatting.
 *
 * @param number - The number to format
 * @param minimumFractionDigits - Minimum decimal places (default: 2)
 * @param maximumFractionDigits - Maximum decimal places (default: 2)
 * @param locale - Optional locale override. If not provided, uses user's country locale.
 * @returns Formatted number string
 */
export function formatNumberForDisplay(
    number: number | string | null | undefined,
    minimumFractionDigits: number = 2,
    maximumFractionDigits: number = 2,
    locale?: string
): string {
    if (number == null || number === '' || number === '-') {
        return '';
    }

    let parsedNumber: number;

    if (typeof number === 'string') {
        // Handle European formatted strings (e.g., "1.625,50" or "1.625")
        // Remove dots (thousands separator) and replace comma with dot (decimal separator)
        const normalizedString = number.replace(/\./g, '').replace(',', '.');
        parsedNumber = parseFloat(normalizedString);
    } else {
        parsedNumber = number;
    }

    if (isNaN(parsedNumber)) {
        return '';
    }

    const formattingLocale = locale || getNumberFormattingLocale();

    let formattedNumber = new Intl.NumberFormat(formattingLocale, {
        minimumFractionDigits,
        maximumFractionDigits,
    }).format(parsedNumber);

    // Normalize the minus sign (some locales use Unicode minus '−' instead of '-')
    if (formattedNumber.includes('−')) {
        formattedNumber = formattedNumber.replace('−', '-');
    }

    return formattedNumber;
}

export function validateAndFormatFloat(value: string, minValue: number, maxValue: number): string {
    const minValueActual = (minValue + 1).toString();
    const maxValueActual = (maxValue - 1).toString();
    const regex = /^-?\d*\.?\d*$/;

    if (value == null || value === '') {
        return minValueActual;
    }
    const regexExecArray: null | RegExpExecArray = regex.exec(value);

    if (regexExecArray == null) {
        return minValueActual;
    }

    let returnValue = regexExecArray[0];

    if (regexExecArray[0] == null || regexExecArray[0] === '.') {
        returnValue = minValueActual;
    }
    if (Number(regexExecArray[0]) >= maxValue) {
        returnValue = maxValueActual;
    }

    return returnValue;
}

export function validateAndFormatInteger(value: FieldNumberValue, minValue: number, maxValue: number) {
    let convertedNumber = Math.floor(Number(value));

    if (isNaN(convertedNumber) || convertedNumber <= minValue) {
        convertedNumber = minValue;
    }

    if (convertedNumber >= maxValue) {
        convertedNumber = maxValue;
    }

    return convertedNumber;
}

function isDecimal(number: number): boolean {
    return !Number.isInteger(number);
}

function isDateValid(date: string) {
    // Define the array of date formats to check
    const formatsToCheck = [
        moment.ISO_8601, // ISO formats
        'MM-DD-YYYY',
        'DD-MM-YYYY',
        'YYYY-MM-DD',
        'YYYY.MM.DD',
        'MM.DD.YYYY',
        'DD.MM.YYYY',
        'YYYY/MM/DD',
        'DD/MM/YYYY',
        'MM/DD/YYYY',
    ];

    for (let format of formatsToCheck) {
        if (moment(date, format, true).isValid()) {
            return { isValid: true, format };
        }
    }

    return { isValid: false };
}

export function formatValueForDashboard(number: number | string, isPrice = false) {
    const parsedNumber = Number(number);
    const locale = getNumberFormattingLocale();

    const minimumFractionDigits = isDecimal(parsedNumber) || isPrice ? 2 : 0;

    if (!isNaN(parsedNumber)) {
        let formattedNumber = formatNumberForDisplay(parsedNumber, minimumFractionDigits, 2, locale);

        return formattedNumber;
    }

    const isValidDate = isDateValid(String(number));

    if (isValidDate.isValid) {
        return moment(String(number), isValidDate.format).format('DD.MM.YYYY');
    }

    return number;
}
